import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SearchableSelect from '@/components/SearchableSelect';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  SkeletonBodyText,
  Box,
  DataTable,
  Button,
  TextField,
  Tabs,
} from '@shopify/polaris';
import { doctorsApi, clinicsApi } from '@/services';
import type { Patient, ClinicDoctor } from '@/types';

export default function DoctorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [associateClinicId, setAssociateClinicId] = useState('');

  const { data: doctor, isLoading: doctorLoading } = useQuery({
    queryKey: ['doctor', id],
    queryFn: () => doctorsApi.getById(id!),
    enabled: !!id,
  });

  const { data: patients, isLoading: patientsLoading } = useQuery({
    queryKey: ['doctor-patients', id],
    queryFn: () => doctorsApi.getPatients(id!, { limit: 20 }),
    enabled: !!id && selectedTab === 1,
  });

  // Clinic picker for the associate dropdown (Clinics tab). Searches on the server,
  // then drops any clinic this doctor is already associated with — previously the
  // whole 842-clinic list was capped at 200 and filtered client-side, so most
  // clinics never appeared at all.
  const [associateClinicName, setAssociateClinicName] = useState('');
  const searchUnassociatedClinics = useCallback(
    async (query: string) => {
      const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
      const alreadyAssociated = new Set(
        (doctor?.clinicAssociations || []).map((assoc) => assoc.clinicId),
      );
      return res.data
        .filter((c) => !alreadyAssociated.has(c.id))
        .map((c) => ({ label: c.name, value: c.id, detail: c.email || undefined }));
    },
    [doctor],
  );

  const updateMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string; email: string; phone: string }) =>
      doctorsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', id] });
      setIsEditing(false);
    },
  });

  const associateMutation = useMutation({
    mutationFn: (clinicId: string) => doctorsApi.associateWithClinic(id!, clinicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', id] });
      setAssociateClinicId('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (clinicId: string) => doctorsApi.removeFromClinic(id!, clinicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', id] });
    },
  });

  if (doctorLoading) {
    return (
      <Page title="Doctor Details">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!doctor) {
    return (
      <Page
        title="Doctor Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/doctors') }}
      >
        <Card>
          <Text as="p">The requested doctor could not be found.</Text>
        </Card>
      </Page>
    );
  }

  const patientRows = (patients?.data || []).map((patient: Patient) => [
    <Button
      key={patient.id}
      variant="plain"
      onClick={() => navigate(`/patients/${patient.id}`)}
    >
      {`${patient.firstName} ${patient.lastName}`}
    </Button>,
    patient.email || 'N/A',
    patient.phone || 'N/A',
    new Date(patient.createdAt).toLocaleDateString(),
  ]);

  const clinicRows = (doctor.clinicAssociations || []).map((assoc: ClinicDoctor) => [
    <Button
      key={assoc.clinicId}
      variant="plain"
      onClick={() => navigate(`/clinics/${assoc.clinicId}`)}
    >
      {assoc.clinic?.name || assoc.clinicId}
    </Button>,
    <Badge key={`status-${assoc.id}`} tone={assoc.status === 'active' ? 'success' : undefined}>
      {assoc.status}
    </Badge>,
    <Button
      key={`remove-${assoc.id}`}
      tone="critical"
      variant="plain"
      onClick={() => removeMutation.mutate(assoc.clinicId)}
      loading={removeMutation.isPending}
    >
      Remove
    </Button>,
  ]);

  const tabs = [
    { id: 'details', content: 'Details', accessibilityLabel: 'Doctor details' },
    { id: 'patients', content: 'Patients', accessibilityLabel: 'Doctor patients' },
    { id: 'clinics', content: 'Clinics', accessibilityLabel: 'Doctor clinics' },
  ];

  const patientCount = doctor._count?.patients ?? doctor.patientCount ?? 0;
  const rentalCount = doctor._count?.rentals ?? doctor.rentalCount ?? 0;

  return (
    <Page
      title={`Dr. ${doctor.firstName} ${doctor.lastName}`}
      backAction={{ content: 'Back', onAction: () => navigate('/doctors') }}
      titleMetadata={
        <Badge tone={doctor.status === 'active' ? 'success' : undefined}>
          {doctor.status}
        </Badge>
      }
      secondaryActions={[
        {
          content: isEditing ? 'Cancel' : 'Edit',
          onAction: () => {
            if (isEditing) {
              setIsEditing(false);
            } else {
              setEditForm({
                firstName: doctor.firstName,
                lastName: doctor.lastName,
                email: doctor.email || '',
                phone: doctor.phone || '',
              });
              setIsEditing(true);
            }
          },
        },
      ]}
    >
      <Layout>
        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Patient Count</Text>
                <Text as="p" variant="headingXl">
                  {patientCount}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Rental Count</Text>
                <Text as="p" variant="headingXl">
                  {rentalCount}
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Main Content */}
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />

            {selectedTab === 0 && (
              <Box padding="400">
                {isEditing ? (
                  <BlockStack gap="400">
                    <TextField
                      label="First Name"
                      value={editForm.firstName}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, firstName: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Last Name"
                      value={editForm.lastName}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, lastName: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={editForm.email}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, email: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Phone"
                      type="tel"
                      value={editForm.phone}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, phone: value }))}
                      autoComplete="off"
                    />
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={() => updateMutation.mutate(editForm)}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                      <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Email</Text>
                        <Text as="span">{doctor.email || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Phone</Text>
                        <Text as="span">{doctor.phone || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Status</Text>
                        <Text as="span">{doctor.status}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Created</Text>
                        <Text as="span">{new Date(doctor.createdAt).toLocaleDateString()}</Text>
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                )}
              </Box>
            )}

            {selectedTab === 1 && (
              <>
                {patientsLoading ? (
                  <Box padding="400">
                    <SkeletonBodyText lines={5} />
                  </Box>
                ) : patientRows.length === 0 ? (
                  <Box padding="800">
                    <Text as="p" tone="subdued" alignment="center">
                      No patients found for this doctor
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Name', 'Email', 'Phone', 'Created']}
                    rows={patientRows}
                  />
                )}
              </>
            )}

            {selectedTab === 2 && (
              <Box padding="400">
                <BlockStack gap="400">
                  {clinicRows.length === 0 ? (
                    <Text as="p" tone="subdued" alignment="center">
                      No clinic associations found
                    </Text>
                  ) : (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text']}
                      headings={['Clinic', 'Status', 'Action']}
                      rows={clinicRows}
                    />
                  )}

                  <BlockStack gap="200">
                    <Text as="p" fontWeight="semibold">Associate with Clinic</Text>
                    <InlineStack gap="200" align="start" blockAlign="end">
                      <SearchableSelect
                        label="Clinic"
                        value={associateClinicId}
                        selectedLabel={associateClinicName}
                        onChange={(value, option) => {
                          setAssociateClinicId(value);
                          setAssociateClinicName(option?.label ?? '');
                        }}
                        onSearch={searchUnassociatedClinics}
                        emptyOption={{ label: 'Select a clinic...', value: '' }}
                        placeholder="Type a clinic name…"
                      />
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (associateClinicId) {
                            associateMutation.mutate(associateClinicId);
                          }
                        }}
                        disabled={!associateClinicId}
                        loading={associateMutation.isPending}
                      >
                        Associate
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
